import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Edit2, AlertTriangle, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-hot-toast';
import profileDataService from '../../../services/profileDataService'; 

const ProfileDataTab = forwardRef(({ 
  profileData, 
  completeness, 
  inconsistencies, 
  onFieldUpdate, 
  expandedSections: externalExpandedSections,
  onSectionToggle 
}, ref) => {
  const [impactForecasts, setImpactForecasts] = useState([]);
  const [editingSection, setEditingSection] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [savingSection, setSavingSection] = useState(null);
  const [focusFieldKey, setFocusFieldKey] = useState(null);
  const [internalExpandedSections, setInternalExpandedSections] = useState({
    basicInfo: true,
    schoolInfo: false,
    academic: false,
    testScores: false,
    coursework: false,
    activities: false,
    goals: false,
    background: false
  });
  
  // Use external expanded sections if provided, otherwise use internal state
  const expandedSections = externalExpandedSections || internalExpandedSections;

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    startEditWithFocus: (sectionId, fieldKey) => {
      // Expand the section if using internal state
      if (!externalExpandedSections) {
        setInternalExpandedSections(prev => ({
          ...prev,
          [sectionId]: true
        }));
      }
      
      // Get current values for the section
      const sectionData = getSectionData(sectionId, profileData);
      const currentValues = {};
      if (sectionData && sectionData.fields) {
        sectionData.fields.forEach(field => {
          currentValues[field.key] = field.value || '';
        });
      }
      
      // Start editing the section
      setEditingSection(sectionId);
      setEditValues(currentValues);
      setFocusFieldKey(fieldKey);
      
      // Focus the field after render
      setTimeout(() => {
        const fieldElement = document.getElementById(`field-input-${fieldKey}`);
        if (fieldElement) {
          fieldElement.focus();
          // If it's a text input, select all text for easy replacement
          if (fieldElement.type === 'text') {
            fieldElement.select();
          }
        }
      }, 100);
    }
  }));

  // Generate impact forecasts when profile data changes
  useEffect(() => {
    if (profileData) {
      try {
        const forecasts = profileDataService.generateImpactForecasts(
          profileData, 
          completeness?.missingFields || {}
        );
        setImpactForecasts(forecasts);
      } catch (error) {
        console.error('Error generating impact forecasts:', error);
        setImpactForecasts([]);
      }
    }
  }, [profileData, completeness]);

  const toggleSection = (section) => {
    if (onSectionToggle) {
      // Use external handler if provided
      onSectionToggle(section, !expandedSections[section]);
    } else {
      // Otherwise use internal state
      setInternalExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    }
  };

  const startEdit = (section, currentValues) => {
    setEditingSection(section);
    setEditValues(currentValues);
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setEditValues({});
    setFocusFieldKey(null);
  };

  const saveEdit = async (section) => {
    try {
      setSavingSection(section);
      
      // Update each field in the section - all fields go to student_academic_profiles
      for (const [field, value] of Object.entries(editValues)) {
        // Convert display values to database values for enum fields
        const dbValue = convertDisplayValueToDb(value, field);
        await onFieldUpdate(field, dbValue, 'academic');
      }
      
      setEditingSection(null);
      setEditValues({});
      setFocusFieldKey(null);
      setSavingSection(null);
      toast.success('Section updated successfully');
      // Remove onRefresh() call - parent component handles state updates optimistically
    } catch (error) {
      console.error('Error saving section:', error);
      setSavingSection(null);
      toast.error('Failed to save changes');
    }
  };

  const getSectionData = (sectionId, data) => {
    // Helper function to get section data by ID
    const sectionConfigs = {
      basicInfo: {
        fields: [
          { key: 'name', value: data?.academic?.name },
          { key: 'grade_level', value: data?.academic?.grade_level },
          { key: 'graduation_year', value: data?.academic?.graduation_year },
          { key: 'state_of_residency', value: data?.academic?.state_of_residency }
        ]
      },
      schoolInfo: {
        fields: [
          { key: 'high_school_name', value: data?.academic?.high_school_name },
          { key: 'high_school_city', value: data?.academic?.high_school_city },
          { key: 'high_school_state', value: data?.academic?.high_school_state },
          { key: 'high_school_type', value: data?.academic?.high_school_type },
          { key: 'hs_competitiveness', value: data?.academic?.hs_competitiveness }
        ]
      },
      academic: {
        fields: [
          { key: 'gpa_weighted', value: data?.academic?.gpa_weighted },
          { key: 'gpa_unweighted', value: data?.academic?.gpa_unweighted },
          { key: 'gpa_scale', value: data?.academic?.gpa_scale },
          { key: 'class_rank', value: data?.academic?.class_rank },
          { key: 'class_size', value: data?.academic?.class_size },
          { key: 'grade_trend', value: data?.academic?.grade_trend }
        ]
      },
      testScores: {
        fields: [
          { key: 'sat_total', value: data?.academic?.sat_total },
          { key: 'sat_math', value: data?.academic?.sat_math },
          { key: 'sat_reading_writing', value: data?.academic?.sat_reading_writing },
          { key: 'act_composite', value: data?.academic?.act_composite },
          { key: 'national_merit_status', value: data?.academic?.national_merit_status }
        ]
      },
      coursework: {
        fields: [
          { key: 'ap_courses_taken', value: data?.academic?.ap_courses_taken },
          { key: 'honors_courses', value: data?.academic?.honors_courses },
          { key: 'dual_enrollment_credits', value: data?.academic?.dual_enrollment_credits },
          { key: 'curriculum_rigor', value: data?.academic?.curriculum_rigor }
        ]
      },
      activities: {
        fields: [
          { key: 'leadership_activities', value: data?.academic?.leadership_activities },
          { key: 'extracurriculars', value: data?.academic?.extracurriculars },
          { key: 'time_commitment', value: data?.academic?.time_commitment },
          { key: 'academic_awards', value: data?.academic?.academic_awards },
          { key: 'special_talents', value: data?.academic?.special_talents }
        ]
      },
      goals: {
        fields: [
          { key: 'intended_major', value: data?.academic?.intended_major },
          { key: 'career_goals', value: data?.academic?.career_goals },
          { key: 'academic_interests', value: data?.academic?.academic_interests },
          { key: 'college_preferences', value: data?.academic?.college_preferences },
          { key: 'financial_aid_needed', value: data?.academic?.financial_aid_needed }
        ]
      },
      background: {
        fields: [
          { key: 'first_generation_college', value: data?.academic?.first_generation_college },
          { key: 'languages', value: data?.academic?.languages },
          { key: 'geographic_region', value: data?.academic?.geographic_region },
          { key: 'special_circumstances', value: data?.academic?.special_circumstances }
        ]
      }
    };
    
    return sectionConfigs[sectionId] || { fields: [] };
  };

  const formatValue = (value, fieldKey) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-slate-400 italic">Not specified</span>;
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    
    // Format enum values for better display
    if (fieldKey === 'hs_competitiveness') {
      const competitivenessLabels = {
        'highly_competitive': 'Highly Competitive',
        'competitive': 'Competitive', 
        'moderate': 'Moderate',
        'less_competitive': 'Less Competitive'
      };
      return competitivenessLabels[value] || value;
    }
    
    if (fieldKey === 'curriculum_rigor') {
      const rigorLabels = {
        'most_rigorous': 'Most Rigorous',
        'very_rigorous': 'Very Rigorous',
        'rigorous': 'Rigorous',
        'moderately_rigorous': 'Moderately Rigorous',
        'less_rigorous': 'Less Rigorous'
      };
      return rigorLabels[value] || value;
    }
    
    if (fieldKey === 'national_merit_status') {
      const meritLabels = {
        'semifinalist': 'Semifinalist',
        'finalist': 'Finalist',
        'scholar': 'Scholar',
        'commended': 'Commended',
        'not_applicable': 'Not Applicable'
      };
      return meritLabels[value] || value;
    }
    
    if (fieldKey === 'grade_trend') {
      const trendLabels = {
        'upward': 'Upward',
        'downward': 'Downward',
        'consistent': 'Consistent',
        'variable': 'Variable'
      };
      return trendLabels[value] || value;
    }
    
    return value;
  };

  const convertDisplayValueToDb = (value, fieldKey) => {
    // Convert user-friendly display values back to database enum values
    if (fieldKey === 'hs_competitiveness') {
      const dbValues = {
        'Highly Competitive': 'highly_competitive',
        'Competitive': 'competitive',
        'Moderate': 'moderate', 
        'Less Competitive': 'less_competitive'
      };
      return dbValues[value] || value;
    }
    
    if (fieldKey === 'curriculum_rigor') {
      const dbValues = {
        'Most Rigorous': 'most_rigorous',
        'Very Rigorous': 'very_rigorous',
        'Rigorous': 'rigorous',
        'Moderately Rigorous': 'moderately_rigorous',
        'Less Rigorous': 'less_rigorous'
      };
      return dbValues[value] || value;
    }
    
    if (fieldKey === 'national_merit_status') {
      const dbValues = {
        'Semifinalist': 'semifinalist',
        'Finalist': 'finalist',
        'Scholar': 'scholar',
        'Commended': 'commended',
        'Not Applicable': 'not_applicable'
      };
      return dbValues[value] || value;
    }
    
    if (fieldKey === 'grade_trend') {
      const dbValues = {
        'Upward': 'upward',
        'Downward': 'downward', 
        'Consistent': 'consistent',
        'Variable': 'variable'
      };
      return dbValues[value] || value;
    }
    
    return value;
  };

  const getFieldOptions = (fieldKey) => {
    switch (fieldKey) {
      case 'hs_competitiveness':
        return ['Highly Competitive', 'Competitive', 'Moderate', 'Less Competitive'];
      case 'curriculum_rigor':
        return ['Most Rigorous', 'Very Rigorous', 'Rigorous', 'Moderately Rigorous', 'Less Rigorous'];
      case 'national_merit_status':
        return ['Semifinalist', 'Finalist', 'Scholar', 'Commended', 'Not Applicable'];
      case 'grade_trend':
        return ['Upward', 'Downward', 'Consistent', 'Variable'];
      default:
        return null;
    }
  };

  const getVerificationStatus = (lastUpdated) => {
    if (!lastUpdated) return 'Never verified';
    
    const daysSince = Math.floor((new Date() - new Date(lastUpdated)) / (1000 * 60 * 60 * 24));
    if (daysSince === 0) return 'Verified today';
    if (daysSince === 1) return 'Verified yesterday';
    if (daysSince < 7) return `Verified ${daysSince} days ago`;
    if (daysSince < 30) return `Verified ${Math.floor(daysSince / 7)} weeks ago`;
    return `Verified ${Math.floor(daysSince / 30)} months ago`;
  };


  const getQuickWins = () => {
    if (!impactForecasts || impactForecasts.length === 0) return [];
    
    return impactForecasts.slice(0, 4).map(forecast => ({
      title: forecast.action,
      description: profileDataService.getQuickWinDescription
        ? profileDataService.getQuickWinDescription(forecast.field)
        : `Complete ${forecast.field} to improve your profile`,
      impact: `+${forecast.impact}%`,
      field: forecast.field,
      section: forecast.section
    }));
  };

  const sections = [
    {
      id: 'basicInfo',
      title: 'Basic Information',
      fields: [
        { key: 'name', label: 'Full Name', value: profileData?.academic?.name },
        { key: 'grade_level', label: 'Grade Level', value: profileData?.academic?.grade_level },
        { key: 'graduation_year', label: 'Graduation Year', value: profileData?.academic?.graduation_year },
        { key: 'state_of_residency', label: 'State of Residency', value: profileData?.academic?.state_of_residency }
      ]
    },
    {
      id: 'schoolInfo',
      title: 'School Information',
      fields: [
        { key: 'high_school_name', label: 'High School Name', value: profileData?.academic?.high_school_name },
        { key: 'high_school_city', label: 'City', value: profileData?.academic?.high_school_city },
        { key: 'high_school_state', label: 'State', value: profileData?.academic?.high_school_state },
        { key: 'high_school_type', label: 'School Type', value: profileData?.academic?.high_school_type },
        { key: 'hs_competitiveness', label: 'School Competitiveness', value: profileData?.academic?.hs_competitiveness }
      ],
      prompt: !profileData?.academic?.hs_competitiveness && 
        'School competitiveness level would help AI better interpret your achievements.'
    },
    {
      id: 'academicPerformance',
      title: 'Academic Performance',
      fields: [
        { key: 'gpa_weighted', label: 'Weighted GPA', value: profileData?.academic?.gpa_weighted },
        { key: 'gpa_unweighted', label: 'Unweighted GPA', value: profileData?.academic?.gpa_unweighted },
        { key: 'gpa_scale', label: 'GPA Scale', value: profileData?.academic?.gpa_scale },
        { key: 'class_rank', label: 'Class Rank', value: profileData?.academic?.class_rank },
        { key: 'class_size', label: 'Class Size', value: profileData?.academic?.class_size }
      ]
    },
    {
      id: 'testScores',
      title: 'Standardized Test Scores',
      fields: [
        { key: 'sat_total', label: 'SAT Total', value: profileData?.academic?.sat_total },
        { key: 'sat_math', label: 'SAT Math', value: profileData?.academic?.sat_math },
        { key: 'sat_reading_writing', label: 'SAT Reading/Writing', value: profileData?.academic?.sat_reading_writing },
        { key: 'act_composite', label: 'ACT Composite', value: profileData?.academic?.act_composite },
        { key: 'national_merit_status', label: 'National Merit Status', value: profileData?.academic?.national_merit_status }
      ]
    },
    {
      id: 'coursework',
      title: 'Coursework & Rigor',
      fields: [
        { key: 'ap_courses_taken', label: 'AP Courses Taken', value: profileData?.academic?.ap_courses_taken },
        { key: 'honors_courses', label: 'Honors Courses', value: profileData?.academic?.honors_courses },
        { key: 'dual_enrollment_credits', label: 'Dual Enrollment Credits', value: profileData?.academic?.dual_enrollment_credits },
        { key: 'curriculum_rigor', label: 'Curriculum Rigor', value: profileData?.academic?.curriculum_rigor }
      ],
      prompt: !profileData?.academic?.curriculum_rigor && 
        'Curriculum rigor level is essential for college admissions context.'
    },
    {
      id: 'activities',
      title: 'Activities & Leadership',
      fields: [
        { key: 'leadership_activities', label: 'Leadership Roles', value: profileData?.academic?.leadership_activities },
        { key: 'extracurriculars', label: 'Key Activities', value: profileData?.academic?.extracurriculars },
        { key: 'time_commitment', label: 'Time Commitment', value: profileData?.academic?.time_commitment },
        { key: 'academic_awards', label: 'Academic Awards', value: profileData?.academic?.academic_awards },
        { key: 'special_talents', label: 'Special Talents', value: profileData?.academic?.special_talents }
      ],
      prompt: !profileData?.academic?.time_commitment && 
        'Adding time commitments for your top activities would demonstrate depth of engagement.'
    },
    {
      id: 'goals',
      title: 'Goals & Aspirations',
      fields: [
        { key: 'intended_major', label: 'Intended Major', value: profileData?.academic?.intended_major },
        { key: 'career_goals', label: 'Career Goals', value: profileData?.academic?.career_goals },
        { key: 'academic_interests', label: 'Academic Interests', value: profileData?.academic?.academic_interests },
        { key: 'college_preferences', label: 'College Preferences', value: profileData?.academic?.college_preferences },
        { key: 'financial_aid_needed', label: 'Financial Context', value: profileData?.academic?.financial_aid_needed }
      ],
      prompt: !profileData?.academic?.financial_aid_needed && 
        'Financial context helps us recommend realistic colleges and scholarship opportunities.'
    },
    {
      id: 'personalBackground',
      title: 'Personal Background',
      fields: [
        { key: 'first_generation_college', label: 'First Generation College', 
          value: profileData?.academic?.first_generation_college === true ? 'Yes' : 
                 profileData?.academic?.first_generation_college === false ? 'No' : null },
        { key: 'languages', label: 'Languages Spoken', value: profileData?.academic?.languages },
        { key: 'geographic_region', label: 'Geographic Context', value: profileData?.academic?.geographic_region },
        { key: 'special_circumstances', label: 'Special Circumstances', value: profileData?.academic?.special_circumstances }
      ]
    }
  ];

  const quickWins = getQuickWins();

  return (
    <div className="space-y-6">
      {quickWins.length > 0 && (
        <div className="bg-gradient-to-br from-violet-50 to-violet-100 border border-violet-200 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 relative overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-violet-900 mb-1">Quick Wins - 5 Minute Improvements</h3>
              <p className="text-sm text-violet-700">Small changes that make a big impact on your college profile</p>
            </div>
            <div className="hidden md:flex items-center gap-2 text-sm text-violet-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"></path>
              </svg>
              Est. {quickWins.length * 5} min total
            </div>
          </div>

          {/* Quick Win Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
            {quickWins.map((win, index) => (
              <div
                key={index}
                className="bg-white border border-stone-200 rounded-xl p-4 sm:p-5 cursor-pointer transition-all duration-300 hover:border-violet-300 hover:shadow-lg hover:shadow-violet-500/10 hover:-translate-y-1 group"
                onClick={() => {
                  // Use the startEditWithFocus method to navigate and focus
                  if (ref && ref.current && ref.current.startEditWithFocus) {
                    ref.current.startEditWithFocus(win.section, win.field);
                  } else {
                    // Fallback: expand section and scroll
                    if (!externalExpandedSections) {
                      setInternalExpandedSections(prev => ({
                        ...prev,
                        [win.section]: true
                      }));
                    }
                    // Start editing
                    const sectionData = getSectionData(win.section, profileData);
                    const currentValues = {};
                    if (sectionData && sectionData.fields) {
                      sectionData.fields.forEach(field => {
                        currentValues[field.key] = field.value || '';
                      });
                    }
                    setEditingSection(win.section);
                    setEditValues(currentValues);
                    setFocusFieldKey(win.field);
                    
                    // Scroll and focus
                    setTimeout(() => {
                      const sectionElement = document.getElementById(win.section);
                      if (sectionElement) {
                        sectionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                      const fieldElement = document.getElementById(`field-input-${win.field}`);
                      if (fieldElement) {
                        fieldElement.focus();
                        if (fieldElement.type === 'text') {
                          fieldElement.select();
                        }
                      }
                    }, 100);
                  }
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-stone-800 mb-2 group-hover:text-violet-700 transition-colors">{win.title}</h4>
                    <p className="text-sm text-stone-600 leading-relaxed">{win.description}</p>
                  </div>
                  <div className="bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-semibold ml-3 shadow-sm">
                    {win.impact}
                  </div>
                </div>
                <div className="flex items-center text-xs text-stone-500 mt-3">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"></path>
                  </svg>
                  ~5 minutes
                </div>
              </div>
            ))}
          </div>

          {/* Action Button */}
          <div className="mt-6 flex justify-center">
            <button className="bg-violet-600 hover:bg-violet-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Complete All Quick Wins
            </button>
          </div>

          {/* Decorative elements */}
          <div className="absolute top-4 right-4 w-20 h-20 bg-violet-200 rounded-full opacity-20 blur-xl"></div>
          <div className="absolute bottom-4 left-4 w-16 h-16 bg-violet-300 rounded-full opacity-15 blur-lg"></div>
        </div>
      )}

      <div className="space-y-4">
      {sections.map((section) => {
        const sectionCompleteness = completeness?.sections?.[section.id];
        const isEditing = editingSection === section.id;
        const hasInconsistency = inconsistencies.some(i => 
          section.fields.some(f => f.key === i.field)
        );
        
        return (
          <div
            key={section.id}
            id={section.id}
            className="bg-white border border-slate-900 rounded-xl overflow-hidden transition-all duration-200 hover:border-indigo-500 hover:shadow-lg"
          >
            <div className={`px-5 py-4 flex justify-between items-center transition-all duration-200 ${
              expandedSections[section.id]
                ? 'bg-slate-900 text-white'
                : 'bg-white text-slate-900'
            }`}>
              <div className="flex items-center gap-2 sm:gap-3 flex-1">
                <button
                  onClick={() => toggleSection(section.id)}
                  className="flex items-center gap-2 flex-1"
                >
                  <div className="flex-1">
                    <h3 className="text-sm sm:text-base font-semibold mb-1">{section.title}</h3>
                    <div className={`text-sm flex items-center gap-3 flex-wrap ${
                      expandedSections[section.id] ? 'opacity-80' : 'opacity-60'
                    }`}>
                      <span>{section.fields.length} fields</span>
                      <div className="flex items-center gap-1.5">
                        <div className={`w-10 h-1 rounded overflow-hidden ${
                          expandedSections[section.id]
                            ? 'bg-white bg-opacity-30'
                            : 'bg-slate-900 bg-opacity-20'
                        }`}>
                          <div
                            className="h-full bg-teal-400 transition-all duration-300"
                            style={{ width: `${sectionCompleteness?.percentage || 0}%` }}
                          />
                        </div>
                        <span>{sectionCompleteness?.percentage || 0}%</span>
                      </div>
                      {hasInconsistency && (
                        <span className="text-amber-400 text-xs">⚠ Needs review</span>
                      )}
                      <span className="text-xs opacity-70">
                        {getVerificationStatus(profileData?.academic?.updated_at)}
                      </span>
                    </div>
                  </div>
                  {expandedSections[section.id] ? (
                    <ChevronUp className="w-4 sm:w-5 h-4 sm:h-5 opacity-60" />
                  ) : (
                    <ChevronDown className="w-4 sm:w-5 h-4 sm:h-5 opacity-60" />
                  )}
                </button>
              </div>
              <div className="flex items-center gap-2 ml-4">
                {!isEditing ? (
                  <button
                    className={`px-3 py-1.5 rounded-md text-sm cursor-pointer transition-all duration-200 ${
                      expandedSections[section.id]
                        ? 'bg-white bg-opacity-20 text-white border border-white border-opacity-30 hover:bg-opacity-30'
                        : 'bg-slate-900 bg-opacity-10 text-slate-900 border border-slate-900 border-opacity-30 hover:bg-opacity-20'
                    }`}
                    onClick={() => startEdit(section.id,
                      Object.fromEntries(section.fields.map(f => [f.key, f.value]))
                    )}
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      className={`px-3 py-1.5 rounded-md text-sm cursor-pointer transition-all duration-200 ${
                        savingSection === section.id
                          ? 'bg-emerald-700 text-white opacity-75 cursor-not-allowed'
                          : 'bg-emerald-600 text-white hover:bg-emerald-700'
                      }`}
                      onClick={() => saveEdit(section.id)}
                      disabled={savingSection === section.id}
                    >
                      {savingSection === section.id ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className={`px-3 py-1.5 rounded-md text-sm cursor-pointer transition-all duration-200 ${
                        expandedSections[section.id]
                          ? 'bg-white bg-opacity-20 text-white border border-white border-opacity-30 hover:bg-opacity-30'
                          : 'bg-slate-900 bg-opacity-10 text-slate-900 border border-slate-900 border-opacity-30 hover:bg-opacity-20'
                      }`}
                      onClick={cancelEdit}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
            
            {expandedSections[section.id] && (
              <div className="p-4 sm:p-5">
                <div className="space-y-3">
                  {section.fields.map((field) => {
                    const inconsistency = inconsistencies.find(i => i.field === field.key);
                    
                    return (
                      <div key={field.key} data-field={field.key}>
                        <div className="flex justify-between items-center py-2 border-b border-slate-100 last:border-b-0">
                          <span className="font-medium text-slate-600 text-sm">
                            {field.label}
                          </span>
                          {isEditing ? (
                            (() => {
                              const options = getFieldOptions(field.key);
                              const currentDisplayValue = formatValue(field.value, field.key);
                              
                              if (options) {
                                // Dropdown for enum fields
                                return (
                                  <select
                                    id={`field-input-${field.key}`}
                                    className="text-slate-800 font-semibold text-sm text-right max-w-[60%] border border-slate-300 rounded px-2 py-1 bg-white"
                                    value={editValues[field.key] || currentDisplayValue || ''}
                                    onChange={(e) => setEditValues(prev => ({
                                      ...prev,
                                      [field.key]: e.target.value
                                    }))}
                                    autoFocus={focusFieldKey === field.key}
                                  >
                                    <option value="">Select...</option>
                                    {options.map(option => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </select>
                                );
                              } else {
                                // Text input for free-form fields
                                return (
                                  <input
                                    id={`field-input-${field.key}`}
                                    type="text"
                                    className="text-slate-800 font-semibold text-sm text-right max-w-[60%] border border-slate-300 rounded px-2 py-1"
                                    value={editValues[field.key] || ''}
                                    onChange={(e) => setEditValues(prev => ({
                                      ...prev,
                                      [field.key]: e.target.value
                                    }))}
                                    autoFocus={focusFieldKey === field.key}
                                  />
                                );
                              }
                            })()
                          ) : (
                            <span className={`text-sm text-right max-w-[60%] ${
                              inconsistency ? 'text-red-600 font-semibold' : 
                              field.value ? 'text-slate-800 font-semibold' : 'text-slate-400 italic'
                            }`}>
                              {formatValue(field.value, field.key)}
                            </span>
                          )}
                        </div>
                        {inconsistency && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                            <span className="text-sm text-red-700">{inconsistency.message}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {section.prompt && (
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 mt-4">
                    <h4 className="text-indigo-900 text-sm font-semibold mb-1">💡 Add {section.title} Context</h4>
                    <div className="text-slate-800 text-sm">{section.prompt}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
  );
});

ProfileDataTab.displayName = 'ProfileDataTab';

export default ProfileDataTab;